import React, { ReactElement } from "react";

import { ButtonTheme } from "../../types/enums/ButtonThemes";
import InputFormOption from "../../types/system/InputFormOption";
import InputFieldTypes, {
    InputFormFieldType,
} from "../../types/enums/ImportFieldTypes";
import RenderOrientations, {
    RenderOrientation,
} from "../../types/enums/RenderOrientation";
import Utilities from "../../foundation/Utility";
import ReactDOMServer from "react-dom/server";
import Checkbox from "./Checkbox";
import RadioList from "./RadioList";
import InputPassword from "./Password";
import Textbox from "./Textbox";
import InputRichText from "./RichText";
import DatePicker from "./DatePicker";
import DropDown, { DropDownOption } from "./DropDown";
import InputItemList from "./InputItemList";
import Button from "../buttons/Button";
import { InputFormContext } from "./InputForm";
import { Guid } from "guid-typescript";
import ColourPicker from "./ColourPicker";
import Utility from "../../foundation/Utility";
import TimePicker from "./TimePicker";

interface InputFormFieldProps {
    blank?: boolean;
    button?: string;
    buttonIcon?: string;
    buttonInfo?: string;
    buttonTheme?: ButtonTheme;
    children?: any;
    columnSpan?: number;
    defaultValue?: any;
    dependsOn?: string;
    disabled?: boolean;
    display?: RenderOrientation;
    displayOnly?: boolean;
    field?: string;
    fieldWidth?: number;
    fullWidth?: boolean;
    height?: string;
    info?: string;
    label?: string;
    labelBold?: boolean;
    mandatory?: boolean;
    maxDate?: Date;
    maxNumber?: number;
    minDate?: Date;
    minNumber?: number;
    allowNull?: boolean;
    naForNull?: boolean;
    decimalPlaces?: number;
    decimalIncrement?: number;
    options?: InputFormOption[];
    reverse?: boolean;
    tooltip?: string;
    tooltipPlace?: string;
    type?: InputFormFieldType;
    value?: any;
    weight?: number;
    hidden?: boolean;

    onChange?: (startValue: any, newValue: any) => void;
    onButtonClick?: () => void;
}

interface InputFormFieldState {
    keyName: string;
    changeListeners: ((startValue: any, newValue: any) => void)[];
}

class InputFormField extends React.Component<
    InputFormFieldProps,
    InputFormFieldState
> {
    static contextType = InputFormContext;
    context!: React.ContextType<typeof InputFormContext>;

    state: InputFormFieldState = {
        keyName: Guid.create().toString(),
        changeListeners: [],
    };

    addChangeListeners(
        listener: (startValue: any, newValue: any) => void,
    ): void {
        if (listener && !this.state.changeListeners.includes(listener))
            this.setState({
                changeListeners: [...this.state.changeListeners, listener],
            });
    }

    valueChanged(value: any) {
        if (!!this.context.binding) {
            try {
                Utilities.setPathToValue(
                    this.props.field,
                    value,
                    this.context.binding,
                );
            } catch {}
        }
        this.props.onChange?.(this.getValue(), value);
        this.context.onChange();
    }

    isDisabled(): boolean {
        if (this.props.disabled) return true;
        if (this.props.dependsOn) {
            const dependsValue = Utilities.getPathToValue(
                this.props.dependsOn,
                this.context.binding,
            );
            if (!dependsValue) return true;
        }
    }

    getValue() {
        return (
            this.props.value ??
            Utilities.getPathToValue(
                this.props?.field ?? "",
                this.context.binding,
            ) ??
            this.props.defaultValue
        );
    }

    getClassess(): string {
        let classes = "";

        if (this.props.fullWidth) classes += " full";
        if (this.props.mandatory) classes += " mandatory-field";

        return classes;
    }

    getCheck(tooltipContent: string | null): JSX.Element {
        return (
            <Checkbox
                id={this.state.keyName}
                key={this.state.keyName}
                value={this.getValue()}
                disabled={this.isDisabled()}
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
                tooltip={tooltipContent}
                tooltipPlace="top"
            />
        );
    }
    getRadio(tooltipContent: string | null): JSX.Element {
        return (
            <RadioList
                id={this.state.keyName}
                key={this.state.keyName}
                value={this.getValue()}
                options={this.props.options}
                disabled={this.isDisabled()}
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
                tooltip={tooltipContent}
                tooltipPlace="top"
                display={this.props.display}
            />
        );
    }

    getText(tooltipContent: string | null): JSX.Element {
        return (
            <Textbox
                id={this.state.keyName}
                key={this.state.keyName}
                type="text"
                width={this.props.fieldWidth}
                value={this.getValue()}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                data-place="top"
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
            />
        );
    }

    getTextMulti(tooltipContent: string | null): JSX.Element {
        return (
            <textarea
                id={this.state.keyName}
                key={this.state.keyName}
                value={this.getValue()}
                style={{ width: this.props.fieldWidth }}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                data-place="top"
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
            />
        );
    }

    getTextRich(tooltipContent: string | null): JSX.Element {
        return (
            <InputRichText
                id={this.state.keyName}
                key={this.state.keyName}
                value={this.getValue()}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                fullWidth={this.props.fullWidth}
                data-place="top"
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
            />
        );
    }

    getPassword(tooltipContent: string | null): JSX.Element {
        return (
            <InputPassword
                id={this.state.keyName}
                key={this.state.keyName}
                value={this.getValue()}
                width={this.props.fieldWidth}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                data-place="top"
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
            />
        );
    }

    getNumber(tooltipContent: string | null): JSX.Element {
        return (
            <Textbox
                id={this.state.keyName}
                key={this.state.keyName}
                type="number"
                width={this.props.fieldWidth}
                value={this.getValue()}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                data-place="top"
                allowNull={this.props.allowNull}
                naForNull={this.props.naForNull}
                decimalPlaces={this.props.decimalPlaces}
                decimalIncrement={this.props.decimalIncrement}
                min={this.props.minNumber}
                max={this.props.maxNumber}
                className={this.getClassess()}
                onChange={value =>
                    this.valueChanged(!!value ? Number(value) : undefined)
                }
            />
        );
    }

    getDate(tooltipContent: string | null): JSX.Element {
        return (
            <DatePicker
                id={this.state.keyName}
                key={this.state.keyName}
                width={this.props.fieldWidth}
                value={this.getValue()}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                data-place="top"
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
            />
        );
    }

    getTime(tooltipContent: string | null): JSX.Element {
        return (
            <TimePicker
                id={this.state.keyName}
                key={this.state.keyName}
                value={this.getValue()}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                data-place="top"
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
            />
        );
    }

    getSelect(tooltipContent: string | null): JSX.Element {
        return (
            <DropDown
                id={this.state.keyName}
                key={this.state.keyName}
                blank={this.props.blank}
                noType={true}
                options={
                    this.props.options?.map(item => item as DropDownOption) ??
                    []
                }
                value={this.getValue()}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                data-place="top"
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
            />
        );
    }

    getList(tooltipContent: string | null): JSX.Element {
        return (
            <InputItemList
                id={this.state.keyName}
                key={this.state.keyName}
                value={this.getValue()}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                data-place="top"
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
            />
        );
    }

    getColour(tooltipContent: string | null): JSX.Element {
        return (
            <ColourPicker
                id={this.state.keyName}
                key={this.state.keyName}
                value={this.getValue()}
                disabled={this.isDisabled()}
                data-tip={tooltipContent}
                data-html={true}
                data-place="top"
                className={this.getClassess()}
                onChange={value => this.valueChanged(value)}
            />
        );
    }

    getInputField(): JSX.Element {
        const value = this.getValue();

        if (this.props.displayOnly) return value;

        let tooltipContent: string | null = null;

        if (
            this.props.display !== RenderOrientations.Vertical &&
            this.props.children
        )
            tooltipContent = ReactDOMServer.renderToString(this.props.children);
        else tooltipContent = this.props.info ?? null;

        if (this.props.type)
            switch (this.props.type) {
                case InputFieldTypes.Check:
                    return this.getCheck(tooltipContent);
                case InputFieldTypes.Date:
                    return this.getDate(tooltipContent);
                case InputFieldTypes.Number:
                    return this.getNumber(tooltipContent);
                case InputFieldTypes.Password:
                    return this.getPassword(tooltipContent);
                case InputFieldTypes.Radio:
                    return this.getRadio(tooltipContent);
                case InputFieldTypes.Select:
                    return this.getSelect(tooltipContent);
                case InputFieldTypes.Text:
                    return this.getText(tooltipContent);
                case InputFieldTypes.TextMulti:
                    return this.getTextMulti(tooltipContent);
                case InputFieldTypes.TextRich:
                    return this.getTextRich(tooltipContent);
                case InputFieldTypes.Time:
                    return this.getTime(tooltipContent);
                case InputFieldTypes.List:
                    return this.getList(tooltipContent);
                case InputFieldTypes.Colour:
                    return this.getColour(tooltipContent);
            }
        else
            switch (typeof value) {
                case "boolean":
                    return this.getCheck(tooltipContent);
                case "number":
                    return this.getNumber(tooltipContent);
                default:
                    if (this.props.options)
                        return this.getSelect(tooltipContent);
                    else if (value instanceof Date)
                        return this.getDate(tooltipContent);
                    else return this.getText(tooltipContent);
            }
    }

    getInputType(): string {
        if (this.props.type)
            switch (this.props.type) {
                case InputFieldTypes.Check:
                    return "check";
                case InputFieldTypes.Date:
                    return "date";
                case InputFieldTypes.Number:
                    return "number";
                case InputFieldTypes.Password:
                    return "password";
                case InputFieldTypes.Radio:
                    return "radio";
                case InputFieldTypes.Select:
                    return "select";
                case InputFieldTypes.Text:
                    return "text";
                case InputFieldTypes.TextMulti:
                    return "text-multi";
                case InputFieldTypes.TextRich:
                    return "text-rich";
                case InputFieldTypes.Time:
                    return "time";
                case InputFieldTypes.List:
                    return "list";
                case InputFieldTypes.Colour:
                    return "colour";
            }
        else {
            const value = this.getValue();

            switch (typeof value) {
                case "boolean":
                    return "check";
                case "number":
                    return "number";
                default:
                    if (this.props.options) return "select";
                    else if (value instanceof Date) return "date";
                    else return "text";
            }
        }
    }

    getButton(): ReactElement<Button> {
        return (
            <Button
                text={this.props.button}
                icon={this.props.buttonIcon}
                disabled={this.props.disabled === true}
                fullWidth={this.props.fullWidth}
                theme={this.props.buttonTheme}
                tooltip={ReactDOMServer.renderToString(this.props.children)}
                tooltipPlace="top"
                onClick={this.props.onButtonClick}
            />
        );
    }

    render(): React.ReactNode {
        return (
            <div
                data-html={true}
                className={`field ${this.props.reverse ? "reversed" : ""}`}
                style={{ flex: this.props.weight }}
                data-tip={this.props.tooltip}
                data-place={this.props.tooltipPlace ?? "top"}>
                {this.props.hidden ? null : (
                    <>
                        {Utility.notNullOrEmpty(this.props.label) && (
                            <label
                                htmlFor={this.state.keyName}
                                className={`field-label${
                                    this.props.labelBold ? " bold" : ""
                                }`}
                                style={{ height: this.props.height }}>
                                <span>
                                    {this.props.label}
                                    {this.props.mandatory ? " *" : ""}
                                </span>
                            </label>
                        )}
                        {(this.props.info || this.props.children) && (
                            <span className="field-info">
                                {this.props.children ?? this.props.info}
                            </span>
                        )}
                        <span
                            className={`field-input${
                                this.props.button ? " has-button" : ""
                            } input-${this.getInputType()}`}
                            style={{ height: this.props.height }}>
                            {this.getInputField()}
                            {this.props.button && this.getButton()}
                        </span>
                    </>
                )}
            </div>
        );
    }
}

export default InputFormField;
